<template>
    <div class="operate-record">
        <div class="operate-record-date fx-v-center">
            <date-picker v-model="datePickerValue" @change="datePickerChange"/>
            <span class="ml-8" v-if="editionJudge.isBiaoZhun">标准版可保留员工7天内的操作记录</span>
            <span class="ml-8" v-if="editionJudge.isZhuanYe">专业版可保留员工90天内的操作记录</span>
            <span class="ml-8" v-if="editionJudge.isQiJian">旗舰版可以保留员工一年内的操作记录</span>
        </div>
        <el-table v-loading="isLoading"
            class="mt-8"
            ref="elTable"
            :height="'calc(100vh - 182px)'"
            :data="tableData"
            :empty-text="isLoading ? ' ' : '暂无数据'">
            <el-table-column
                label="时间"
                prop="operate_time"
                min-width="170">
                <template v-slot="{ row }">
                    <p>{{ row.operate_time }}</p>
                </template>
            </el-table-column>
            <el-table-column
                label="操作人"
                prop="operator"
                min-width="200">
                <template v-slot:header="{ row }">
                    <table-data-filter v-model="operatePersonSelected"
                        allString="所有"
                        :title="'操作人'"
                        :width="200"
                        :height="360"
                        :items="operatePerson"
                        :itemKeyName="'user'"
                        :searchKey="['nickname', 'realname']"
                        @confirm="filterConfirm">
                        <template v-slot="{ row }">
                            <user-info maxWidth="130px" :userInfo="row" />
                        </template>
                    </table-data-filter>
                </template>
                <template v-slot="{ row }">
                    <user-info :userInfo="row.operator.user"
                        :canClick="true"
                        :url="'/main/staff/staffDetail?id=' + row.operator.id"/>
                </template>
            </el-table-column>
            <el-table-column
                label="操作模块"
                prop="operate_type_text"
                min-width="100">
                <template v-slot:header="{ row }">
                    <table-data-filter v-model="operateModuleSelected"
                        allString="所有"
                        :title="'操作模块'"
                        :width="200"
                        :height="360"
                        :items="operateModule"
                        @confirm="filterConfirm"/>
                </template>
                <template v-slot="{ row }">
                    <p class="text-popper" v-textPopper>{{ getModuleType(row.source) }}</p>
                </template>
            </el-table-column>
            <el-table-column
                label="操作内容"
                prop="detail"
                min-width="500">
                <template v-slot="{ row }">
                    <span class="fw-600">{{ row.operate_type_text }}</span>
                    <!-- 订单退款操作详情 -->
                    <template v-if="get(row, 'detail.order_refund_info')">
                        <!-- 退款 -->
                        (订单<span class="href" @click="showOrderFormDetail(get(row, 'detail.order_refund_info.order_no'))">{{ get(row, 'detail.order_refund_info.order_no') }}</span>，退款{{ get(row, 'detail.order_refund_info.refund_money') }}元)
                    </template>
                    <!-- 订单商品操作记详情 -->
                    <template v-if="get(row, 'detail.operate_goods_info')">
                        <!-- 删除挂单 -->
                        <template v-if="row.operate_type === 1">
                            (挂单中包含<render-goods :goodsInfo="get(row, 'detail.operate_goods_info')" />{{ NoToChinese(get(row, 'detail.operate_goods_info.length')) }}种商品，共计{{ +getSumByKey(get(row, 'detail.operate_goods_info'), 'subtotal') }}元)
                        </template>
                        <!-- 删除挂单内商品 -->
                        <template v-if="row.operate_type === 2">
                            ({{ get(row, 'detail.operate_goods_info').map(item => `${item.goods_name}，${item.price}元/${get(item, 'sale_price.sale_unit.name')}，${item.amount}${get(item, 'sale_price.sale_unit.name')}，小计${item.subtotal}元`).join('；') }})
                        </template>
                    </template>
                    <!-- 商品价格操作详情 -->
                    <template v-if="get(row, 'detail.price_change_info')">
                        <!-- 临时改价-按折扣改 -->
                        <template v-if="row.operate_type === 3">
                            ({{ get(row, 'detail.price_change_info').map(item => `${item.goods_name}售价修改为${fixNumPro(item.sale_price)}元/${get(item, 'sale_unit.name')}`).join('；') }})
                        </template>
                        <!-- 临时改价-按金额改 -->
                        <template v-if="row.operate_type === 4">
                            ({{ get(row, 'detail.price_change_info').map(item => `${item.goods_name}售价修改为${fixNumPro(item.sale_price)}元/${get(item, 'sale_unit.name')}`).join('；') }})
                        </template>
                        <!-- 永久改价 -->
                        <template v-if="row.operate_type === 5">
                            ({{ 
                                get(row, 'detail.price_change_info.length') === 1
                                ? get(row, 'detail.price_change_info').map(item => `${item.goods_name}售价修改为${fixNumPro(item.sale_price)}元/${get(item, 'sale_unit.name')}`).join('；')
                                : `${get(row, 'detail.price_change_info[0].goods_name')}售价修改为:${get(row, 'detail.price_change_info').map((item, i) => `${['一级', '二级', '三级'][i]}，购买量≥${item.threshold}，${fixNumPro(item.sale_price)}元/${get(item, 'sale_unit.name')}`).join('；')}`
                            }})
                        </template>
                    </template>
                    <!-- 订单折扣操作详情 -->
                    <template v-if="get(row, 'detail.order_discount_info')">
                        <!-- 整单折扣 -->
                        <template v-if="row.operate_type === 6">
                            (订单<span class="href" @click="showOrderFormDetail(get(row, 'detail.order_discount_info.order.order_no'))">{{ get(row, 'detail.order_discount_info.order.order_no') }}</span>，商品总价{{ get(row, 'detail.order_discount_info.order.subtotal') }}元，折扣[{{ fixNumPro(get(row, 'detail.order_discount_info.discount_rate') * 10) }}折]，优惠{{ get(row, 'detail.order_discount_info.discount_amount') }}元)
                        </template>
                        <!-- 整单减免 -->
                        <template v-if="row.operate_type === 7">
                            (订单<span class="href" @click="showOrderFormDetail(get(row, 'detail.order_discount_info.order.order_no'))">{{ get(row, 'detail.order_discount_info.order.order_no') }}</span>，商品总价{{ get(row, 'detail.order_discount_info.order.subtotal') }}元，优惠{{ get(row, 'detail.order_discount_info.discount_amount') }}元)
                        </template>
                        <!-- 整单抹零 -->
                        <template v-if="row.operate_type === 8">
                            (订单<span class="href" @click="showOrderFormDetail(get(row, 'detail.order_discount_info.order.order_no'))">{{ get(row, 'detail.order_discount_info.order.order_no') }}</span>，商品总价{{ get(row, 'detail.order_discount_info.order.subtotal') }}元，优惠{{ get(row, 'detail.order_discount_info.discount_amount') }}元)
                        </template>
                    </template>
                    <!-- 挂单商品数量修改详情 -->
                    <template v-if="get(row, 'detail.goods_amount_change_info')">
                        <!-- 修改挂单内商品数/重量 -->
                        <template v-if="row.operate_type === 9">
                            ({{ get(row, 'detail.goods_amount_change_info.goods_name') }}，{{ get(row, 'detail.goods_amount_change_info.origin_amount') }}{{ get(row, 'detail.goods_amount_change_info.sale_price.sale_unit.name') }}修改为{{ get(row, 'detail.goods_amount_change_info.modified_amount') }}{{ get(row, 'detail.goods_amount_change_info.sale_price.sale_unit.name') }})
                        </template>
                    </template>
                    <!-- 商品导入详情 -->
                    <template v-if="get(row, 'detail.goods_import_info')">
                        <template v-if="row.operate_type === 16">
                            {{ get(row, 'detail.operate_type_text') }}(导入商品{{ get(row, 'detail.goods_import_info.goods_imported') }}条)
                            <router-link class="href pos-right"
                                :to="{
                                    name: 'goodsImportOperateDetail',
                                    params: {
                                        from: $route.path
                                    },
                                    query: {
                                        recordId: get(row, 'detail.goods_import_info.import_record_id')
                                    }
                                }">查看详情
                            </router-link>
                        </template>
                    </template>
                </template>
            </el-table-column>
            <p v-if="isHasMore && isScrollLoading" slot="append" style="text-align: center; padding: 10px;">
                <span class="loading-step-animation">正在加载...</span>
            </p>
            <p v-if="(!isHasMore) && tableData.length > 0" slot="append" style="text-align: center; padding: 10px;">没有更多了</p>
        </el-table>
        <!-- component -->
        <order-form-detail :orderNo="orderNo" :visible.sync="isShowOrderFormDetail"/>
    </div>
</template>

<script lang="tsx">
import { createComponent, ref, onBeforeMount, onMounted } from '@vue/composition-api'
import DatePicker from '@/components/date_picker/date_picker.vue'
import TableDataFilter from '@/components/table_data_filter/table_data_filter.vue'
import OrderFormDetail from '@/views/order_form/order_form_detail/order_form_detail.vue'
import { openMessage, useScrollLoad } from '@/utils/system.tsx'
import { getOneMonthValue, getSumByKey, fixNumPro, NoToChinese } from '@/utils'
import { $fetch } from '@/api'
import clone from 'lodash/cloneDeep'
import get from 'lodash/get'
import dayjs from 'dayjs'
import { editionJudge } from '../../../utils/editionJudge'

export default createComponent({
    setup: (props, ctx) => {
        // reactive data
        const isLoading = ref(false)
        const isShowOrderFormDetail = ref(false)
        const orderNo = ref<string>('')
        const datePickerValue = ref<any[]>(getOneMonthValue())
        const tableData = ref<any[]>([])
        const operatePerson = ref<any[]>([])
        const operateModule = ref<any[]>([{
            id: 0,
            name: '收银台'
        }, {
            id: 1,
            name: '后台-订单'
        }, {
            id: 2,
            name: 'APP-订单'
        }, {
            id: 3,
            name: 'APP-会员'
        }, {
            id: 4,
            name: '后台-商品'
        }])
        const operatePersonSelected = ref<any[]>([])
        const operateModuleSelected = ref<any[]>([0, 1, 2, 3, 4])
        const { page: oPage, isLoading: isScrollLoading, isHasMore, bindListener, initScrollLoad } = useScrollLoad()

        // not reactive data and methods
        let oLimit: number = 20
        let oStartDate: string = ''
        let oEndDate: string = ''

        function format(t?: string) {
            return dayjs(t).format('YYYY-MM-DD')
        }

        onBeforeMount(() => {
            oLimit = 20
            oStartDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD')
            oEndDate = format()
            isLoading.value = true
            getStaffList()
                .then(() => getStaffOperateRecords())
                .then(() => isLoading.value = false)
        })

        onMounted(() => {
            bindListener({
                dom: (ctx.refs.elTable as any).$el.querySelector('.el-table__body-wrapper'),
                loadFunction: getStaffOperateRecords
            })
        })

        // reactive methods
        async function getStaffList() {
            try {
                const result = await $fetch.post({
                    url: '/staff/getstafflist',
                    params: {
                        page: 1,
                        limit: Math.pow(10, 8)
                    }
                })
                console.log('员工列表', clone(result))
                operatePersonSelected.value = result.staff_list.map(item => item.id)
                operatePerson.value = result.staff_list
            } catch (e) {
                openMessage('error', e.message || '获取员工列表失败')
            }
        }

        async function getStaffOperateRecords(page = oPage.value, limit = oLimit, from_date = oStartDate, to_date = oEndDate, operator_ids = operatePersonSelected.value, sources = operateModuleSelected.value) {
            try {
                const result = await $fetch.post({
                    url: '/staff/staffoperaterecords',
                    params: {
                        page,
                        limit,
                        from_date,
                        to_date,
                        operator_ids,
                        sources
                    }
                })
                console.log('员工操作记录', clone(result))
                if (page === 1) {
                    tableData.value = result.operate_records
                    initScrollLoad(result)
                } else {
                    tableData.value = tableData.value.concat(result.operate_records)
                }

                return result
            } catch (e) {
                openMessage('error', e.message || '获取员工列表失败')
            }
        }

        function getModuleType(n: number) {
            const moduleType = operateModule.value.find(item => item.id === n)
            return moduleType ? moduleType.name : `未知操作模块: ${n}`
        }

        function datePickerChange() {
            oPage.value = 1
            if (datePickerValue.value === null) {
                // 清空的情况
                oStartDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD')
                oEndDate = format()
            } else {
                oStartDate = format(datePickerValue.value[0])
                oEndDate = format(datePickerValue.value[1])
            }
            isLoading.value = true
            getStaffOperateRecords()
                .then(() => isLoading.value = false)
        }

        function filterConfirm() {
            oPage.value = 1
            isLoading.value = true
            getStaffOperateRecords()
                .then(() => isLoading.value = false)
        }

        function showOrderFormDetail(id: string) {
            orderNo.value = id
            isShowOrderFormDetail.value = true
        }

        return {
            // data
            isLoading,
            isScrollLoading,
            isHasMore,
            isShowOrderFormDetail,
            orderNo,
            datePickerValue,
            tableData,
            operatePerson,
            operateModule,
            operatePersonSelected,
            operateModuleSelected,
            editionJudge,
            // methods
            get,
            getSumByKey,
            fixNumPro,
            datePickerChange,
            getModuleType,
            filterConfirm,
            showOrderFormDetail,
            NoToChinese
        }
    },

    components: {
        DatePicker,
        TableDataFilter,
        OrderFormDetail,
        RenderGoods: {
            props: {
                goodsInfo: {
                    type: Array,
                    required: true
                }
            },
            render() {
                const goodsInfo = (this as any).$props.goodsInfo
                const len = goodsInfo.length

                if (len <= 10) {
                    return <span>{ goodsInfo.map(item => item.goods_name).join('、') }</span>
                } else {
                    return (
                        <el-popover trigger="hover"
                            placement="top"
                            width="500">
                            <span>{ goodsInfo.map(item => item.goods_name).join('、') }</span>
                            <span slot="reference">{ goodsInfo.slice(0, 10).map(item => item.goods_name).join('、') + '...' }</span>
                        </el-popover>
                    )
                }
            }
        }
    }
})
</script>

<style lang="scss" scoped>
.operate-record {
    padding: 8px 12px;
    font-size: 14px;
    color: #333;

    ::v-deep .table-data-filter {
        width: 90px;
    }
}
.pos-right {
    position: absolute;
    right: 20px;
}
</style>
